import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BarChart3, Loader2, Info } from "lucide-react";

const TABS = [
  ["overview", "Overview"], ["traffic", "Traffic"], ["acquisition", "Acquisition"],
  ["users", "Users"], ["marketplace", "Marketplace"], ["matching", "Matching"],
  ["leads", "Leads"], ["ai", "AI"], ["social", "Social"], ["spotlight", "Spotlight"],
];
const WK = ["today", "7d", "30d"];
const WLABEL = { today: "Today", "7d": "7 Days", "30d": "30 Days" };
const fmt = (v) => (v == null ? "—" : v);
const pct = (v) => (v == null ? "—" : `${(Number(v) * 100).toFixed(1)}%`);

const Kpi = ({ label, value, testid, rate }) => (
  <div className="bg-card border border-border rounded-xl p-4" data-testid={testid}>
    <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
    <div className="font-display text-2xl font-bold text-slate-900 mt-1">{rate ? pct(value) : fmt(value)}</div>
  </div>
);

// Renders a metrics × window table. rows: [key, label, isRate?]
function WindowTable({ data, rows, testid }) {
  if (!data?.windows) return null;
  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="bg-secondary/50">
          <tr>
            <th className="text-left px-4 py-2.5 text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Metric</th>
            {WK.map((w) => <th key={w} className="text-right px-4 py-2.5 text-[11px] uppercase tracking-wide text-slate-500 font-semibold">{WLABEL[w]}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map(([key, label, isRate]) => (
            <tr key={key} className="border-t border-border">
              <td className="px-4 py-2.5 text-slate-700">{label}</td>
              {WK.map((w) => <td key={w} className="px-4 py-2.5 text-right font-medium text-slate-900">{isRate ? pct(data.windows[w]?.[key]) : fmt(data.windows[w]?.[key])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const StartNote = ({ start }) => start ? (
  <p className="text-[11px] text-slate-400 flex items-center gap-1.5"><Info className="h-3 w-3" /> Historical analytics begin {start}. Periods before this show “—” (not measured), not zero.</p>
) : null;

function TabBody({ k }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    setData(null); setErr(false);
    api.get(`/admin/analytics/${k}`).then((r) => alive && setData(r.data)).catch(() => alive && setErr(true));
    return () => { alive = false; };
  }, [k]);

  if (err) return <div className="text-sm text-critical" data-testid="analytics-error">Could not load this section.</div>;
  if (!data) return <div className="flex items-center gap-2 text-slate-500 text-sm" data-testid="analytics-loading"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;

  if (k === "overview") {
    const g = data.growth || {}, a = data.activation || {}, c = data.connecting || {}, v = data.converting || {}, r = data.retention || {};
    return (
      <div className="space-y-6" data-testid="analytics-overview">
        <div><h3 className="font-semibold text-slate-800 mb-2 text-sm">Are we growing? (30D)</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi label="Visitors" value={g.visitors_30d} testid="ov-visitors" />
            <Kpi label="Page Views" value={g.pageviews_30d} testid="ov-pageviews" />
            <Kpi label="Guests" value={g.guests_30d} testid="ov-guests" />
            <Kpi label="New Registrations (30D)" value={g.new_registrations_30d} testid="ov-regs" />
          </div></div>
        <div><h3 className="font-semibold text-slate-800 mb-2 text-sm">Are users activating?</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi label="Verified Pros" value={a.professional_verified} />
            <Kpi label="Activated" value={a.activated} />
            <Kpi label="Activation Rate" value={a.activation_rate} rate />
            <Kpi label="Verification Rate" value={a.verification_rate} rate />
          </div></div>
        <div><h3 className="font-semibold text-slate-800 mb-2 text-sm">Are buyers & properties connecting? (30D)</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi label="Public Inventory" value={c.public_inventory} />
            <Kpi label="Listing Views" value={c.listing_views_30d} />
            <Kpi label="Inquiries" value={c.inquiries_30d} />
            <Kpi label="Collaborations" value={c.collaborations_30d} />
          </div></div>
        <div><h3 className="font-semibold text-slate-800 mb-2 text-sm">Are connections converting?</h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Kpi label="Total Leads" value={v.leads_total} />
            <Kpi label="Win Rate" value={v.win_rate} rate />
            <Kpi label="Match Action Rate (30D)" value={v.match_action_rate_30d} rate />
            <Kpi label="WAU/MAU" value={r.wau_mau} rate />
          </div></div>
        <div className="grid grid-cols-3 gap-3">
          <Kpi label="DAU" value={r.dau} /><Kpi label="WAU" value={r.wau} /><Kpi label="MAU" value={r.mau} />
        </div>
        <StartNote start={data.instrumentation_start} />
      </div>
    );
  }
  if (k === "traffic") return (<div className="space-y-3"><WindowTable testid="tbl-traffic" data={data} rows={[["visitors","Unique Visitors"],["authenticated_visitors","Authenticated"],["guest_visitors","Guests"],["sessions","Sessions"],["page_views","Page Views"]]} /><StartNote start={data.instrumentation_start} /></div>);
  if (k === "acquisition") return (
    <div className="space-y-4" data-testid="analytics-acquisition">
      <h3 className="font-semibold text-slate-800 text-sm">Top Sources (30D, by unique visitors)</h3>
      <div className="bg-card border border-border rounded-2xl overflow-hidden">
        <table className="w-full text-sm"><thead className="bg-secondary/50"><tr>
          <th className="text-left px-4 py-2.5 text-[11px] uppercase text-slate-500 font-semibold">Source</th>
          <th className="text-left px-4 py-2.5 text-[11px] uppercase text-slate-500 font-semibold">Medium</th>
          <th className="text-right px-4 py-2.5 text-[11px] uppercase text-slate-500 font-semibold">Visitors</th></tr></thead>
          <tbody>{(data.by_source_medium || []).map((r, i) => (
            <tr key={i} className="border-t border-border"><td className="px-4 py-2.5 text-slate-700">{r.source}</td><td className="px-4 py-2.5 text-slate-500">{r.medium}</td><td className="px-4 py-2.5 text-right font-medium">{r.visitors}</td></tr>
          ))}
          {(!data.by_source_medium || data.by_source_medium.length === 0) && <tr><td colSpan={3} className="px-4 py-6 text-center text-slate-400 text-sm">No traffic recorded yet.</td></tr>}
          </tbody></table>
      </div>
    </div>
  );
  if (k === "users") {
    const f = data.funnel || {}, r = data.retention || {};
    return (<div className="space-y-5" data-testid="analytics-users">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Kpi label="Professionals" value={f.professionals_total} /><Kpi label="Email Verified" value={f.email_verified} />
        <Kpi label="Pro Verified" value={f.professional_verified} /><Kpi label="Activated" value={f.activated} />
        <Kpi label="Activation Rate" value={f.activation_rate} />
      </div>
      <WindowTable testid="tbl-users" data={data} rows={[["new_professionals","New Professionals"],["new_consumers","New Consumers"]]} />
      <div className="grid grid-cols-4 gap-3"><Kpi label="DAU" value={r.dau} /><Kpi label="WAU" value={r.wau} /><Kpi label="MAU" value={r.mau} /><Kpi label="WAU/MAU" value={r.wau_mau} rate /></div>
      <StartNote start={data.instrumentation_start} />
    </div>);
  }
  if (k === "marketplace") return (<div className="space-y-3"><Kpi label="Current Public Inventory" value={data.current_public_inventory} testid="mkt-inventory" /><WindowTable testid="tbl-marketplace" data={data} rows={[["listing_views","Listing Views"],["unique_listing_viewers","Unique Viewers"],["saves","Saves"],["shares","Shares"],["inquiries","Inquiries"],["viewing_requests","Viewing Requests"],["listing_inquiry_rate","Inquiry Rate",true],["new_listings","New Listings"],["new_buyer_needs","New Buyer Needs"],["zero_result_searches","Zero-Result Searches"]]} /><StartNote start={data.instrumentation_start} /></div>);
  if (k === "matching") return (<div className="space-y-3"><WindowTable testid="tbl-matching" data={data} rows={[["matches_viewed","Matches Viewed"],["matches_acted","Matches Acted"],["match_action_rate","Match Action Rate",true],["collaborations_created","Collaborations"]]} /><p className="text-[11px] text-slate-400">{data.note}</p></div>);
  if (k === "leads") return (
    <div className="space-y-4" data-testid="analytics-leads">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Total Leads" value={data.total} /><Kpi label="Win Rate" value={data.win_rate} rate />
        <Kpi label="New (7D)" value={data.windows?.["7d"]?.new_leads} /><Kpi label="New (30D)" value={data.windows?.["30d"]?.new_leads} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        {Object.entries(data.by_stage || {}).map(([s, n]) => <Kpi key={s} label={s} value={n} />)}
      </div>
      <p className="text-[11px] text-slate-400">{data.source}</p>
    </div>
  );
  if (k === "ai") return (<div className="space-y-3"><WindowTable testid="tbl-ai" data={data} rows={[["listing_assistant_opened","Assistant Opened"],["listing_assistant_applied","Assistant Applied"],["ai_apply_rate","AI Apply Rate",true],["sales_coach_viewed","Coach Viewed"],["sales_coach_action_clicked","Coach Action Clicked"]]} /><p className="text-[11px] text-slate-400">{data.note}</p></div>);
  if (k === "social") return (<div className="space-y-3"><WindowTable testid="tbl-social" data={data} rows={[["share_modal_opened","Share Modal Opened"],["facebook","Facebook"],["linkedin","LinkedIn"],["x","X"],["copy_link","Copy Link"],["native","Native Share"],["social_referred_sessions","Social-Referred Sessions"]]} /><StartNote start={data.instrumentation_start} /></div>);
  if (k === "spotlight") return (
    <div className="space-y-3" data-testid="analytics-spotlight">
      <div className="bg-accent/10 border border-accent/30 rounded-xl p-4 flex items-start gap-2">
        <Info className="h-4 w-4 text-accent mt-0.5 shrink-0" /><p className="text-sm text-slate-700">{data.message}</p>
      </div>
      <WindowTable testid="tbl-spotlight" data={data} rows={[["impressions","Impressions"],["unique_reach","Unique Reach"],["clicks","Clicks"],["ctr","CTR",true]]} />
    </div>
  );
  return null;
}

export function AnalyticsView() {
  const [tab, setTab] = useState("overview");
  const [since, setSince] = useState(null);
  useEffect(() => { api.get("/admin/analytics/definitions").then((r) => setSince(r.data?.instrumentation_start)).catch(() => {}); }, []);
  return (
    <div data-testid="analytics-view">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-4">
        <div className="flex items-center gap-2"><BarChart3 className="h-5 w-5 text-accent" /><h2 className="font-display text-xl font-semibold text-slate-900">Analytics</h2></div>
        {since && <span data-testid="analytics-since" className="text-[11px] text-slate-400">Analytics data available since {since}</span>}
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex flex-wrap h-auto bg-secondary/50 mb-4">
          {TABS.map(([k, l]) => <TabsTrigger key={k} value={k} data-testid={`atab-${k}`} className="data-[state=active]:bg-[#0F2C59] data-[state=active]:text-white">{l}</TabsTrigger>)}
        </TabsList>
        {TABS.map(([k]) => <TabsContent key={k} value={k}>{tab === k && <TabBody k={k} />}</TabsContent>)}
      </Tabs>
    </div>
  );
}
